import { EmptyState, ErrorState, FreshnessBar, LoadingState } from "../components/AdminPageStates";
import { useSystemHealth } from "../hooks/useSystemHealth";
import {
  FounderInfrastructureDriver,
  FounderInfrastructureMetric,
  FounderInfrastructureMetricStatus
} from "../services/systemHealthService";

function statusLabel(status: FounderInfrastructureMetricStatus) {
  switch (status) {
    case "healthy":
      return "Healthy";
    case "warning":
      return "Warning";
    case "action_needed":
      return "Action Needed";
    case "processing":
      return "Processing";
    default:
      return "Unavailable";
  }
}

function MetricCard({ metric }: { metric: FounderInfrastructureMetric }) {
  return (
    <details className={`founder-infrastructure-card ${metric.status}`}>
      <summary>
        <span className="founder-infrastructure-card-label">{metric.title}</span>
        <span className={`status-badge ${metric.status.replace("_", "-")}`}>{statusLabel(metric.status)}</span>
      </summary>

      <p className="founder-infrastructure-value">{metric.value}</p>
      <p className="founder-infrastructure-detail">{metric.detail}</p>

      <div className="founder-infrastructure-drilldown">
        <span>{metric.drilldown}</span>
        <small>{metric.source}</small>
      </div>
    </details>
  );
}

function CostDriverCard({ driver, index }: { driver: FounderInfrastructureDriver; index: number }) {
  return (
    <details className={`founder-infrastructure-driver ${driver.status}`}>
      <summary>
        <span>{index + 1}</span>
        <strong>{driver.title}</strong>
        <em>{driver.value}</em>
      </summary>
      <p>{driver.detail}</p>
    </details>
  );
}

function MetricSection({ title, subtitle, metrics }: { title: string; subtitle: string; metrics: FounderInfrastructureMetric[] }) {
  return (
    <section className="founder-infrastructure-section">
      <div className="founder-infrastructure-section-header">
        <p className="eyebrow">{title}</p>
        <h2>{subtitle}</h2>
      </div>

      <div className="founder-infrastructure-grid">
        {metrics.map((metric) => (
          <MetricCard key={metric.id} metric={metric} />
        ))}
      </div>
    </section>
  );
}

export default function SystemHealth() {
  const { data, error, isLoading, refresh } = useSystemHealth();

  if (isLoading && !data) {
    return <LoadingState message="Loading Founder Infrastructure." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No Founder Infrastructure data is available yet." />;
  }

  const infrastructure = data.infrastructure;

  return (
    <section className="founder-infrastructure-page" aria-label="Founder Infrastructure">
      <FreshnessBar
        dataSource="adminAnalytics platform health metrics with read-only aggregate counts from users, bookings, posts, notifications, challenges, and Stripe webhook events"
        isLive={false}
        lastUpdated={infrastructure.lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />

      <header className="founder-infrastructure-hero">
        <p className="eyebrow">Founder Infrastructure</p>
        <h2>Understand what Chor costs to run.</h2>
        <p>
          Business diagnostics translate infrastructure activity into cost, demand, and efficiency signals. Tap any card
          for the source and calculation context.
        </p>
      </header>

      <MetricSection title="Cost" subtitle="What is Chor spending right now?" metrics={infrastructure.primaryMetrics} />

      <MetricSection title="Usage" subtitle="Where is infrastructure being used?" metrics={infrastructure.usageMetrics} />

      <MetricSection title="Efficiency" subtitle="How efficiently is Chor serving activity?" metrics={infrastructure.derivedMetrics} />

      <section className="founder-infrastructure-section">
        <div className="founder-infrastructure-section-header">
          <p className="eyebrow">Top Cost Drivers</p>
          <h2>The five areas most likely to shape infrastructure spend.</h2>
        </div>

        <div className="founder-infrastructure-drivers">
          {infrastructure.topCostDrivers.map((driver, index) => (
            <CostDriverCard key={driver.id} driver={driver} index={index} />
          ))}
        </div>
      </section>
    </section>
  );
}
